import { Box, Flex, Text } from '@chakra-ui/react';
import { faComment } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dateFormat from 'dateformat';
import {
  Accordion,
  Badge,
  ButtonText,
  OutlineCard,
  Table
} from '@morressier/alembic';
import {
  IChangeRequestData,
  IChangeRequestWithSubmissionData
} from '@publishing-engine/domain/models';
import { IMySubmissionTab } from 'src/constants';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import FileListDisplay from 'src/components/FileListDisplay';
import TAB_FIELD_MAP from 'src/utils/tabFieldMap';
import ProtectedAction from 'src/components/ProtectedAction';

const scrollToField = (field: string) => {
  const element = document.getElementById(`generic-field-label-${field}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

const EmptyChangeRequest = () => {
  return (
    <Flex flexDirection="column" gap={8} paddingBottom={8}>
      <Box bgColor="neutralSecondaryFill" padding={8}>
        <FontAwesomeIcon icon={faComment} fontSize={40} />
      </Box>
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingTitle">No Requests Available</Text>
        <Text textStyle="textBody">
          There are no updates required for your Submission as of now. You will
          be notified if your Submission requires any updates or revisions.
        </Text>
      </Flex>
    </Flex>
  );
};

interface ChangeRequestContentProps {
  changeRequestData: IChangeRequestData;
  checkFormFieldValueIsUpdated: (fieldName: string) => boolean;
  setActiveTab: (tab: IMySubmissionTab) => void;
  submissionFields: ISubmissionFieldsResponse;
  submissionId: string;
}

const ChangeRequestContent = ({
  changeRequestData,
  checkFormFieldValueIsUpdated,
  setActiveTab,
  submissionFields,
  submissionId
}: ChangeRequestContentProps) => {
  const { reason, fields, attachments } = changeRequestData;

  const handleEdit = (field: string) => {
    const tabName = TAB_FIELD_MAP.author[field];
    if (tabName) {
      setActiveTab(tabName);
      // need to add delay, so UI will switch to specific tab and render the tab content
      setTimeout(() => {
        scrollToField(field);
      }, 100);
    }
  };

  return (
    <Flex paddingTop={2} gap={4} flexDirection="column">
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingItem">Response from Editor</Text>
        <Text>{reason}</Text>
      </Flex>

      {!!attachments?.length && (
        <Box marginTop={2}>
          <Text textStyle="headingItem">Attached Files</Text>
          <Flex flexDirection="column" marginTop={2} gap={2}>
            <FileListDisplay files={attachments} displayFieldName="name" />
          </Flex>
        </Box>
      )}

      <Text textStyle="headingItem">Changes Requested</Text>

      <Table>
        <Table.Body borderTop="none">
          {fields.map(field => {
            const isUpdated = checkFormFieldValueIsUpdated(field);
            const formattedFieldName =
              submissionFields[field]?.formFieldDefinition?.params?.labels
                ?.main || field;
            return (
              <Table.Row
                key={field}
                _first={{ borderTop: 'none' }}
                _last={{ borderBottom: 'none' }}
                flex={1}
                gap={2}
                padding={2}
                display="flex"
              >
                <Table.RowCell flex={1} padding={2}>
                  <Text textStyle="headingItem">{formattedFieldName}</Text>
                </Table.RowCell>
                <Table.RowCell flex={1} padding={2}>
                  {isUpdated ? (
                    <Badge variant="success" size="context">
                      Updated
                    </Badge>
                  ) : (
                    <Badge variant="attention" size="context">
                      Not Yet Updated
                    </Badge>
                  )}
                </Table.RowCell>
                <Table.RowCell flex={1} flexShrink={1} padding={2}>
                  <ProtectedAction
                    relation="author"
                    entityType="submission"
                    entityId={submissionId}
                  >
                    <ButtonText
                      whiteSpace={'normal'}
                      wordBreak={'break-word'}
                      textAlign={'left'}
                      onClick={() => handleEdit(field)}
                    >
                      Edit {formattedFieldName}
                    </ButtonText>
                  </ProtectedAction>
                </Table.RowCell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </Flex>
  );
};

interface SubmissionChangeRequestProps {
  changeRequest?: IChangeRequestWithSubmissionData;
  checkFormFieldValueIsUpdated: (fieldName: string) => boolean;
  setActiveTab: (tab: IMySubmissionTab) => void;
  submissionFields: ISubmissionFieldsResponse;
  submissionId: string;
}

const ChangeRequests = (props: SubmissionChangeRequestProps) => {
  const {
    changeRequest,
    checkFormFieldValueIsUpdated,
    setActiveTab,
    submissionFields,
    submissionId
  } = props;

  if (!changeRequest?.requestHistory?.length) {
    return <EmptyChangeRequest />;
  }

  const expandAccordionIndexes =
    changeRequest.requestHistory?.map((_, index) => index) || [];
  const items = changeRequest.requestHistory?.map(changeRequest => {
    return {
      title: `Request Sent on ${dateFormat(
        changeRequest.date,
        'mmmm d, yyyy'
      )}`,
      subtitle: '',
      content: (
        <ChangeRequestContent
          submissionFields={submissionFields}
          changeRequestData={changeRequest}
          submissionId={submissionId}
          checkFormFieldValueIsUpdated={checkFormFieldValueIsUpdated}
          setActiveTab={setActiveTab}
        />
      )
    };
  });

  return (
    <Flex flexDirection="column" gap={4}>
      <OutlineCard padding={4} width="100%">
        <Flex flexDirection="column" gap={6}>
          <Text textStyle="headingSection">Submission Change Request</Text>
          <Text>
            The Editorial Team needs you to make specific changes to your
            Submission. These adjustments are necessary for your work to be
            published. Please address them promptly to speed up the publishing
            process.
          </Text>

          <Accordion
            key={`change-request-accordion`}
            data-testId="change-requests"
            chakraAccordionProps={{
              allowMultiple: true,
              defaultIndex: expandAccordionIndexes,
              style: {
                display: 'flex',
                flexDirection: 'column'
              }
            }}
            accordionItemProps={{
              marginBottom: 0
            }}
            items={items}
          />
        </Flex>
      </OutlineCard>
    </Flex>
  );
};

export default ChangeRequests;
